import { useState } from 'react';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Label } from './ui/label';
import { Copy, Download, Volume2, Loader2, Upload } from 'lucide-react';
import { toast } from 'sonner';
import { SUPPORTED_LANGUAGES, type LanguageCode } from '@/lib/languages';
import { supabase } from '@/integrations/supabase/client';

const ImageTranslation = () => {
  const [targetLanguage, setTargetLanguage] = useState<LanguageCode>('en');
  const [extractedText, setExtractedText] = useState('');
  const [translatedText, setTranslatedText] = useState('');
  const [loading, setLoading] = useState(false);
  const [translating, setTranslating] = useState(false);
  const [speaking, setSpeaking] = useState(false);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [imageFile, setImageFile] = useState<File | null>(null);

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!['image/png', 'image/jpeg', 'image/jpg'].includes(file.type)) {
      toast.error('Please upload PNG, JPG, or JPEG files only.');
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be under 5MB. Please compress or choose a smaller image.');
      return;
    }

    setImageFile(file);
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result as string);
    };
    reader.readAsDataURL(file);
    setExtractedText('');
    setTranslatedText('');
  };

  const handleExtractText = async () => {
    if (!imageFile) {
      toast.error('Please upload an image first!');
      return;
    }

    setLoading(true);
    try {
      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64Image = (reader.result as string).split(',')[1];

        const { data, error } = await supabase.functions.invoke('extract-text-from-image', {
          body: { image: base64Image },
        });

        if (error) throw error;

        if (!data.extractedText || data.extractedText.trim() === '') {
          toast.error('No readable text found in this image. Please try a clearer image.');
          setExtractedText('');
        } else {
          setExtractedText(data.extractedText);
          toast.success('Text extracted successfully!');
        }
      };
      reader.readAsDataURL(imageFile);
    } catch (error: any) {
      console.error('Extraction error:', error);
      toast.error('Failed to extract text. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleTranslate = async () => {
    if (!extractedText.trim()) {
      toast.error('No text to translate. Please extract text from an image first!');
      return;
    }

    setTranslating(true);
    try {
      const { data, error } = await supabase.functions.invoke('translate-text', {
        body: {
          text: extractedText,
          sourceLanguage: 'auto-detect',
          targetLanguage,
          isImageTranslation: true,
        },
      });

      if (error) throw error;

      setTranslatedText(data.translatedText);
      toast.success('Translation complete!');
    } catch (error: any) {
      console.error('Translation error:', error);
      const errorMsg = error.message || error.error || JSON.stringify(error);
      if (errorMsg.toLowerCase().includes('trial')) {
        toast.error('Your trial has ended. Subscribe to continue using the translator.');
      } else {
        toast.error('Something went wrong. Please try again in a moment.');
      }
    } finally {
      setTranslating(false);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(translatedText);
    toast.success('Copied to clipboard!');
  };

  const handleDownload = () => {
    const blob = new Blob([translatedText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'translation.txt';
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Downloaded successfully!');
  };

  const handleSpeak = () => {
    if ('speechSynthesis' in window) {
      setSpeaking(true);
      const utterance = new SpeechSynthesisUtterance(translatedText);
      utterance.lang = targetLanguage;
      utterance.onend = () => setSpeaking(false);
      speechSynthesis.speak(utterance);
    } else {
      toast.error('Text-to-speech not supported in your browser');
    }
  };

  return (
    <div className="space-y-6">
      <div className="space-y-4">
        <div>
          <Label>Upload Image</Label>
          <div className="border-2 border-dashed rounded-lg p-8 text-center hover:border-primary/50 transition-colors">
            <input
              type="file"
              accept="image/png,image/jpeg,image/jpg"
              onChange={handleImageUpload}
              className="hidden"
              id="image-upload"
            />
            <label htmlFor="image-upload" className="cursor-pointer">
              <Upload className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
              <p className="text-sm text-muted-foreground mb-2">
                Click to upload or drag & drop image
              </p>
              <p className="text-xs text-muted-foreground">
                PNG, JPG, JPEG (max 5MB)
              </p>
            </label>
          </div>
        </div>

        {imagePreview && (
          <div className="relative">
            <img
              src={imagePreview}
              alt="Preview"
              className="max-h-64 mx-auto rounded-lg shadow-md"
            />
          </div>
        )}

        {imageFile && (
          <Button onClick={handleExtractText} disabled={loading} className="w-full">
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Extracting text...
              </>
            ) : (
              'Extract Text'
            )}
          </Button>
        )}
      </div>

      {extractedText && (
        <div className="space-y-4">
          <div>
            <Label>Extracted Text (Editable)</Label>
            <Textarea
              value={extractedText}
              onChange={(e) => setExtractedText(e.target.value)}
              className="min-h-[150px] resize-none"
            />
          </div>

          <div>
            <Label>Target Language</Label>
            <Select value={targetLanguage} onValueChange={(value) => setTargetLanguage(value as LanguageCode)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SUPPORTED_LANGUAGES.map((lang) => (
                  <SelectItem key={lang.code} value={lang.code}>
                    {lang.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <Button onClick={handleTranslate} disabled={translating} className="w-full">
            {translating ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Translating...
              </>
            ) : (
              'Translate Extracted Text'
            )}
          </Button>
        </div>
      )}

      {translatedText && (
        <div>
          <Label>Translation</Label>
          <div className="relative">
            <Textarea
              value={translatedText}
              readOnly
              className="min-h-[150px] resize-none bg-muted/30"
            />
            <div className="absolute top-2 right-2 flex gap-2">
              <Button size="icon" variant="ghost" onClick={handleCopy}>
                <Copy className="h-4 w-4" />
              </Button>
              <Button size="icon" variant="ghost" onClick={handleSpeak} disabled={speaking}>
                <Volume2 className="h-4 w-4" />
              </Button>
              <Button size="icon" variant="ghost" onClick={handleDownload}>
                <Download className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImageTranslation;
